import { useEffect, useRef, useState } from "react";

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'

import { ChatBubbleLeftRightIcon, PlayIcon, StopIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { IconSpinner } from "../components/icons";
import { useIpSearch } from "../search/search";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { useAssetLocation } from "./location";
import { family_member, patent_family } from "../data";
import { fullFamilyToString, fullMemberToString, longMemberToString, sanitizeFamily, sanitizeMember, smallFamilyToString } from "../search/fullResult";
import { Link } from "../navigation/";
import { splitRegex, useRenderAsLink } from "./rendering";
import { ChatProvider } from "./ChatApi";
import { ChatItem, ChatItemType, FunctionCall, useChat } from "./ChatContext";
import { useRoles } from "../user/Auth";

/*
TODO
* Save examples to DB for further training
* Make it configurable
*/

/*
  THE GRAPH

  START:
    - get user input
    - indicate waiting state
    - analyze question to decide what to do. The returned tool decides the next step/edge
    - go to <tool>
  answer:
    - get answer from the model
    - go back to END
  ip_search:
    - search for search terms
    - take top 5 entries and add them as tool calls
    - go back to answer
  summarize:
    - (only available if there is an IP asset in focus)
    - retrieve the IP asset in focus
    - retrieve full information about it (incl. claims, costs etc.)
    - go back to answer
  END: 
    - clean up (waiting state, abort signal etc.)
    - go to START
 */

export default function ChatButton() {
  const {hasChat} = useRoles()

  if (!hasChat) return null

  return (
    <ChatProvider>
      <ActualChatButton />
    </ChatProvider>
  )
}

function ActualChatButton() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === '/') {
        e.preventDefault()
        setIsOpen(o => !o)
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <>
      <button onClick={() => setIsOpen(!isOpen)} className="p-1 hover:bg-pcx-400 group rounded-md" title="Chat Bot - Ctrl+Shift+/">
        <ChatBubbleLeftRightIcon className="text-slate-300 group-hover:text-white size-5" />
      </button>
      {isOpen && <Chat {...{ setIsOpen, isOpen }} />}
    </>
  )
}

function Chat({ setIsOpen, isOpen }) {

    const inputRef = useRef<HTMLInputElement | null>(null)
    // autoFocus on input doesn't seem to work
    useEffect(() => {
        if (inputRef?.current !== undefined) {
            inputRef.current.focus()
        }
    }, [inputRef])

    const bottomRef = useRef<HTMLDivElement | null>(null);
    function scrollDown() {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }

    const abortController = useRef(new AbortController())

    const [input, setInput] = useState('')
    const [chatItems, setChatItems] = useState([
        //{ content: 'what is up with the sky?', role: 'user' as ChatItemType },
        //{ content: exampleText, role: 'assistant' as ChatItemType},
    ])
    const [lastItem, setLastItem] = useState(undefined as ChatItem | undefined)
    const [isWaiting, setIsWaiting] = useState(false)

    const {search} = useIpSearch()
    const location = useAssetLocation()

    const {renderAsLink} = useRenderAsLink()
    const {analyzeQuestion, getAnswer} = useChat()

    function close() {
        setIsOpen(false)
    }

    //console.log(atob(ndjson))

    async function START() {
      setIsWaiting(true)
      const runningItems = [
        ...chatItems, 
        { content: input, role: 'user' },
      ]
      // relying on chatItems is error prone as it is non updated immediately
      setChatItems(runningItems)
      setInput('')
      return () => analyze(input, runningItems)
    }

    async function analyze(question: string, runningItems: ChatItem[]) {
      //console.log('analyzing...')
      const result = await analyzeQuestion({ question: input, chatItems, hasIpAsset: location.entity !== undefined })
      const analyzedItems = [...runningItems, result]
      setChatItems(analyzedItems)

      const tc = result.tool_calls?.[0]
      if (tc?.function.name === 'ip_search') {
        //console.log('about to search...')
        return () => ip_search(question, analyzedItems, tc.function)
      } else if (tc?.function.name === 'summarize') {
        //console.log('about to summarize...')
        return () => summarize(question, analyzedItems)
      } else if (tc?.function.name === 'answer') {
        //console.log('about to answer...')
        return () => answer(question, analyzedItems)
      }
    }

    async function ip_search(question: string, runningItems: ChatItem[], functionCall: FunctionCall) {
        //console.log('searching...')
        // TODO: check also for ip type etc.
        const searchTerm = functionCall.arguments.search_term.split(' ').filter(s => s.length > 0).join('|')
        // TODO: not just members but everything
        const results = search(searchTerm)
        const families = results.families.map(f => sanitizeFamily(f.item)).slice(0, 5)
        const members = results.members.map(m => sanitizeMember(m.item)).slice(0, 5)

        const tool_call = {role: 'tool' as ChatItemType, content: JSON.stringify({members, families})}
        const resultItems = [...runningItems, tool_call]
        setChatItems(resultItems)

        //console.log({chatItems, state: 'after search'})
        const questionWithContext = [`User Question: ${question}`,
          '\nSearch Results:\n', 
          'PATENTS',
          `${members.map(r => longMemberToString(r)).join('\n-----\n\n')}`,
          '\n============\nPATENT FAMILIES',
          `${families.map(r => smallFamilyToString(r)).join('\n-----\n\n')}`,
        ].join('\n')
        return () => answer(questionWithContext, resultItems)

    }

    async function summarize(question: string, runningItems: ChatItem[]) {
        //console.log('summarizing...')
        let questionWithContext = question
        let asset = undefined
        let resultsItems = runningItems
        let asset_str = undefined
        if (location.entity === family_member && location.member) {
            asset = location.member
            asset_str = fullMemberToString(location.member)
        } else if (location.entity === patent_family && location.family) {
            asset = location.family
            asset_str = fullFamilyToString(location.family)
        }

        if (asset_str) {
            questionWithContext = `User Question: ${question}\n\nIP Asset: ${asset_str}`
            resultsItems = [...runningItems, { role: 'tool', content: JSON.stringify(asset) }]
        }
        return () => answer(questionWithContext, resultsItems)
    }

    async function answer(question: string, chatItems: ChatItem[]) {
      //console.log('answering...')
      //console.log({chatItems, state: 'before answer'})
      const responses = getAnswer({ question, chatItems, abortController: abortController.current })
      setLastItem({ content: '...', role: 'assistant' })
      let totalResponse = ''
      let buffer = ''
      // const splitRegex = /([ \t\n\r\f\v;,.?!])/;
      for await (const response of responses) {
        buffer += response
        //const tokens = buffer.split(/(\W+)/) // Words, spaces, punctuation,....
        const tokens = buffer.split(splitRegex) // Words, spaces, punctuation,....
        buffer = tokens.pop()
        totalResponse += tokens.map(t => renderAsLink(t)).join('')
        // totalResponse += response
        setLastItem({ content: totalResponse + '...', role: 'assistant' })
        scrollDown()
      }
      const chatItem = { content: totalResponse, role: 'assistant' }
      setLastItem(undefined)
      setChatItems(cs => [...cs, chatItem])
      return // () => END()
    }

    async function END() {
      setLastItem(undefined)
      abortController.current = new AbortController()
      setIsWaiting(false)
    }

    async function walkGraph(edge: () => Promise<() => Promise<any> | undefined>) {
        try {
            const node = await edge()
            scrollDown()
            if (node) {
                return await walkGraph(node)
            }
        } catch (error) {
            console.error(error)
            setChatItems(cs => [...cs, { content: error + '', role: 'error' }])
        } finally {
            await END()
            scrollDown()
        }
    }

    async function sendMessages() {
        return walkGraph(START)
    }

    async function onKeyDown(e) {
        // console.log('on change...')
        if (e.key === 'Enter' && isWaiting === false) {
            return sendMessages()
        } else if (e.key === 'Escape') {
            close()
        }
    }

    //console.log({lastItem, chatItems})

    return (
      <Dialog open={isOpen} onClose={close} className="relative z-50 flex items-center justify-center">
        <DialogBackdrop className="fixed inset-0 bg-black/30" />

        {/* Full-screen container to center the panel */}
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4 xxx">
          {/* The actual dialog panel  */}
          <DialogPanel className="min-w-md max-w-2xl divide-y divide-gray-500 divide-opacity-10 bg-white/90 rounded-xl overflow-hidden">
            <div className={clsx("flex flex-col gap-2 max-h-[80vh] overflow-y-auto px-4", (chatItems.length > 0 || isWaiting || lastItem !== undefined) && "pt-4")}>
              {chatItems.map((chatItem, index) => (
                <ChatItemView key={index} chatItem={chatItem} />
              ))}
              {lastItem && <ChatItemView chatItem={lastItem} waiting={lastItem.content === '...'} />}
              {isWaiting && lastItem === undefined && <IconSpinner className="animate-spin size-6 text-slate-600 shrink-0" />}
              <div ref={bottomRef}></div>
            </div>

            <div className="relative">
              <button onClick={close} className="absolute left-4 top-3.5 group">
                <ChatBubbleLeftRightIcon className="size-5 text-gray-900 text-opacity-40 group-hover:text-opacity-100" />
              </button>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={isWaiting}
                className="h-12 w-full py-2 px-11 text-gray-900 placeholder:text-slate-500 bg-white/50 focus:ring-0 focus:outline-none"
                placeholder="Type your message here"
              />
              {isWaiting ? (
                <button className={buttonStyle} onClick={() => abortController.current?.abort()}>
                  <StopIcon className="size-3" />
                </button>
              ) : (
                <button className={buttonStyle} onClick={sendMessages}>
                  <PlayIcon className="size-3 " />
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    )
}

const buttonStyle = "btn-primary rounded-full p-1 absolute right-4 top-3.5 h-fit"

function ChatItemView({ chatItem, waiting = false }: { chatItem: ChatItem, waiting?: boolean }) {
  //console.log({chatItem})
  const { showTechnicalItems: showTechnicals } = useChat()
  if (!showTechnicals && (['system', 'tool'].includes(chatItem.role) || chatItem.tool_calls?.length > 0)) return null

  if (chatItem.tool_calls?.length > 0) {
    // TODO or null later on; configurable
    return (
      <div className="space-y-2">
        {chatItem.tool_calls.map((tool_call, index) => (
          <div key={index} className="p-2 bg-amber-200 text-amber-900 rounded-md">
            {JSON.stringify(tool_call)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'p-4 chat-answer shadow space-y-2',
        waiting && 'animate-pulse',
        chatItem.role === 'user'
        ? 'ml-10 rounded-lg bg-pcx-200 text-pcx-900'
        : chatItem.role === 'error'
        ? 'rounded-md bg-warn-100 text-warn-900'
        : chatItem.role === 'tool'
        ? 'rounded-md bg-green-100 text-green-900 overflow-x-hidden max-w-full min-h-14'
        : 'text-slate-900 rounded-lg bg-white mr-10',
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>
        {chatItem.content}
      </ReactMarkdown>
    </div>
  )
}

function MarkdownLink({children, href, ...props}) {
  return (
    <Link to={href} {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
      {children}
    </Link>
  )
}
